/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import azURL from '../../../config/serviceAPI';
import environmentConfig from '../../../config/environment';
import { routePaths } from '../../../constants/routePaths';
import { parseUrl } from '../../../utils/urlHelpers';
import { getHeaderDataFromCache } from '@/features/header/api/getHeader';
import { type QueryClient } from '@tanstack/react-query';
import { localStorage } from '@/utils/localStorage';
import { signout } from '@/actions/signout';
import { isRouteProtected } from '@/protectedRoutes';
import { getAcesFlag } from '@/features/ymme/utils/acesFlags';
import { getOMSFlags } from '@/features/product/utils/getOMSFlags';
import { getGCPPriceFlags } from '@/features/product/utils/getGCPPriceFlags';
import { getExperimentId } from '@/utils/searchExperiment';
import type { LegacyRouter } from '@/hooks/useLegacyRouter';
import { getForteDecisionFlag } from '@/features/forte/api/getForteDecision';

const API_CALL_PATH_PREFIX = '/ecomm/b2c';
const idleSessionTimeout = environmentConfig.IDLE_SESSION_TIMEOUT;
const startSession = azURL('startSession');
let lastAPICall = Date.now();
let newSessionRequest: null | Promise<any> = null;

const createSessionRefreshInterceptor = (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  dispatch: Function,
  legacyRouter: LegacyRouter,
  appRouter: AppRouterInstance,
) => {
  return async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const headerData = getHeaderDataFromCache(queryClient);
    // @ts-expect-error possible bug or misleading typings
    const { url } = parseUrl(config.url);
    const isStartSessionCall = config.url === startSession;
    const isApiCall = url.includes(API_CALL_PATH_PREFIX);
    const isProtectedRoute = isRouteProtected(legacyRouter.asPath);
    const { acesEnabled } = getAcesFlag(queryClient);
    const { useOMSInventoryQohService } = getOMSFlags(queryClient);
    const { useGCPPriceService } = getGCPPriceFlags(queryClient);
    const rawExperimentId = getForteDecisionFlag(queryClient, 'experimentId');
    const experimentId = getExperimentId(rawExperimentId, acesEnabled);

    // TODO: Remove this code once ACES is enabled for all users (or hardcode to true)
    if (isApiCall) {
      config.headers.set('X-Aces-Enabled', `${acesEnabled}`);
      config.headers.set('useInventoryQohService', `${!!useOMSInventoryQohService}`);
      config.headers.set('X-Price-Enabled', `${!!useGCPPriceService}`);

      if (acesEnabled && experimentId) {
        config.headers.set('experimentId', experimentId);
      }
    }

    if (isStartSessionCall || !isApiCall) {
      return config;
    }

    const now = Date.now();

    const skuDetailsRoutes = [
      '/[...seoUrlPath]',
      routePaths.searchResult,
      '/products/[...slug]',
      '/brands/[brandName]',
    ];
    if (
      skuDetailsRoutes.includes(legacyRouter.route) &&
      now - lastAPICall <= 500 &&
      config.url?.includes('/browse/sku/details')
    ) {
      lastAPICall = new Date(-8640000000000000).valueOf();
    }

    if (newSessionRequest) {
      await newSessionRequest;
    } else {
      const sessionExpired = now - lastAPICall > idleSessionTimeout;

      if (sessionExpired) {
        if (legacyRouter.asPath === routePaths.checkout) {
          appRouter.push(routePaths.cart);
        }
        if (newSessionRequest == null) {
          newSessionRequest = new Promise((resolve, reject) => {
            const refreshSession = async () => {
              if (
                headerData?.myAccountMap?.autoLogin !== 'true' &&
                headerData?.myAccountMap?.anonymous === 'false'
              ) {
                dispatch(signout(queryClient));
                if (isProtectedRoute) {
                  appRouter.push(routePaths.signIn);
                } else {
                  localStorage.setItem('canShowToastNotification', 'true');
                }
              }

              await axiosInstance.get(startSession);
            };
            refreshSession().then(resolve).catch(reject);
          });
        }

        await newSessionRequest;
        // eslint-disable-next-line require-atomic-updates
        newSessionRequest = null;
      }
    }

    // eslint-disable-next-line require-atomic-updates
    lastAPICall = now;
    return config;
  };
};

export default createSessionRefreshInterceptor;
